import React, { useState, useContext } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { AuthContext } from '../../Context/AuthContext';
import logo from '../../assets/Icons/logo.svg';
import { useNavigate } from 'react-router-dom';


const LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState(1); // 1: email input, 2: password input
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useContext(AuthContext); // Added loginWithGoogle
  const navigate = useNavigate();


  const handleGoogleSuccess = async (credentialResponse) => {
  try {
    setLoading(true);
    setError('');
    
    await loginWithGoogle(credentialResponse.credential);
    
    // Close modal and redirect
    onClose();
    navigate('/Socialmedia/dashboard');
  } catch (err) {
    console.error('Google login error:', err);
    setError('Google authentication failed. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again.');
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the login function from AuthContext
      await login(email, password);
      
      // On successful login, close the modal
      onClose();
    } catch (err) {
      // Handle different error cases
      if (err.response) {
        // The request was made and the server responded with a status code
        if (err.response.status === 401) {
          setError('Invalid email or password. Please try again.');
        } else if (err.response.status === 429) {
          setError('Too many attempts. Please try again later.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else if (err.request) {
        // The request was made but no response was received
        setError('Network error. Please check your connection.');
      } else {
        // Something happened in setting up the request
        setError('Login failed. Please try again.');
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (step === 1 && email) {
      // Basic email validation
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
      setStep(2);
      setError('');
    } else if (step === 2) {
      handleLogin();
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setStep(1);
    setError('');
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="relative bg-white rounded-xl p-8 w-full max-w-md mx-auto">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <h1 className="text-3xl font-extrabold text-center mb-4 text-gray-800">
            Built to Help You<br />Scale By Passion
          </h1>
          <h1 className="text-3xl font-bold text-center mb-8 text-[#FD5F20]">
            Powered by Ato
          </h1>
          
          <div className="flex flex-col space-y-5">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="filled_blue"
              size="large"
              text="continue_with"
              shape="rectangular"
              width="100%"
              locale="en"
              useOneTap={false}
              disabled={loading}
            />
            
            <div className="flex items-center justify-center">
              <hr className="flex-grow border-gray-400" />
              <span className="mx-3 text-gray-500 text-sm">OR</span>
              <hr className="flex-grow border-gray-400" />
            </div>

            <form onSubmit={handleEmailSubmit}>
              {step === 1 ? (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full border border-gray-300 rounded-xl p-3 text-center font-bold focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent outline-none transition-all"
                  required
                  disabled={loading}
                />
              ) : (
                <>
                  <div className="mb-2 text-sm text-gray-600 text-center">
                    Welcome back, {email}
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full border border-gray-300 rounded-xl p-3 text-center font-bold focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent outline-none transition-all"
                    required
                    disabled={loading}
                  />
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-sm text-gray-500 hover:text-[#FD5F20]"
                    >
                      Use a different email
                    </button>
                  </div>
                </>
              )}
              
              {error && (
                <div className="mt-2 text-sm text-red-500 text-center">
                  {error}
                </div>
              )}
              
              <button 
                type="submit"
                className="w-full bg-[#FD5F20] hover:bg-[#e0551d] text-white rounded-xl py-3 px-4 font-bold transition-colors duration-300 mt-4 flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  step === 1 ? 'Continue with Email' : 'Sign In'
                )}
              </button>
            </form>
          </div>
          
          <p className="text-sm text-center mt-6 text-gray-600">
            By Continuing, you agree to Ato's <br/> <a href="/Policy/TermsOfService" className="text-gray-400 hover:underline">Consumers Terms</a> and <a href="/Policy/UsagePolicy" className="text-gray-400 hover:underline">Usage Policy</a>, and acknowledge our <a href="/Policy/PrivacyPolicy" className="text-gray-400 hover:underline">Privacy Policy</a>.
          </p>
          
          <div className="text-center mt-6">
            <img 
              src={logo}
              alt="Ato Creative" 
              className="mx-auto h-10" 
            />
            <p className="mt-2 text-sm text-[#FD5F20]">The Future of Creative Scaling</p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginModal;